import { getBillTo, GetBillToApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import Toaster from "@insite/mobius/Toast/Toaster";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import PhoneNumberField, { PhoneNumberState } from "@preferredMedical/Components/Fields/PhoneNumberField";
import { EventEx } from "@preferredMedical/Models/Misc/EventEx";
import { LicenseUploadRequest } from "@preferredMedical/Models/Shipping/LicenseUploadRequest";
import { sendLicenseUploadRequest } from "@preferredMedical/Services/ShippingService";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface LicenseUploadProps {
    emailRecipients: string;
    title: string;
    description: string;
    requiredFieldsLabel: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

type Props = LicenseUploadProps & ReturnType<typeof mapStateToProps>;

export interface LicenseUploadStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    oneLinerGridItem?: GridItemProps;
    twoLinerGridItem?: GridItemProps;
    uploadGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    requestButton?: ButtonPresentationProps;
}

export const licenseUploadStyles: LicenseUploadStyles = {
    modal: {
        size: 800,
        cssOverrides: {
            modalTitle: css`
                border-bottom: 0;
                padding-bottom: 0px;
                justify-content: center;
            `,
            titleButton: css`
                display: none;
            `,
            modalContent: css`
                padding: 20px 30px;
            `,
        },
    },
    container: { gap: 10 },
    oneLinerGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
            justify-content: center;
        `,
    },
    twoLinerGridItem: {
        width: 6,
        css: css`
            padding-left: 10px;
        `,
    },
    submitButton: {
        css: css`
            width: 90%;
        `,
    },
    requestButton: {
        css: css`
            width: 80%;
            padding: 12px 0;
            font-weight: bold;
            font-size: 16px;
            background-color: #005580;
            color: white;
    
            &:hover {
                background-color: #003c5a;
            }
        `,
    },
    uploadGridItem: {
        width: 12,
        css: css`
            padding-left: 10px;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            > span {
                font-size: 15px;
                font-weight: 600;
            }
        `,
    },
};

interface LicenseUploadState {
    styles: LicenseUploadStyles;
    isAuthenticated: boolean;

    contactName: string;
    contactEmail: string;
    contactNumber: string;
    companyName: string;
    customerNumber: string;
    additionalComments: string;
    files: File[];
    contactNumberOnChange: EventEx;
    contactNumberUpdate: EventEx;

    contactNameError: string;
    contactEmailError: string;
    contactNumberError: string;
    companyNameError: string;
    filesError: string;

    modalIsOpen: boolean;
    inProgress: boolean;

    billToId?: string;
}

const phoneRegex = new RegExp("[0-9]{3}-[0-9]{3}-[0-9]{4}");
const emailRegex = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

class LicenseUploadModal extends React.Component<Props, LicenseUploadState> {
    constructor(props: Props) {
        super(props);
        this.state = {
            styles: licenseUploadStyles,
            isAuthenticated: (props.session.isAuthenticated || props.session.rememberMe) && !props.session.isGuest,

            contactName: "",
            contactEmail: "",
            contactNumber: "",
            companyName: "",
            customerNumber: "",
            additionalComments: "",
            files: [],
            contactNumberOnChange: new EventEx(),
            contactNumberUpdate: new EventEx(),

            contactNameError: "",
            contactEmailError: "",
            contactNumberError: "",
            companyNameError: "",
            filesError: "",

            modalIsOpen: false,
            inProgress: false,
            billToId: props.session.billToId ? props.session.billToId : "",
        };

        this.state.contactNumberOnChange.subscribe(async (phoneNumberState: PhoneNumberState) => {
            await this.setState({ contactNumber: phoneNumberState.value });
            await this.setState({ contactNumberError: phoneNumberState.error });
        });

        this.getCompanyInformation = this.getCompanyInformation.bind(this);
        this.resetFormData = this.resetFormData.bind(this);
        this.modalCloseHandler = this.modalCloseHandler.bind(this);
        this.openModal = this.openModal.bind(this);
        this.checkForErrors = this.checkForErrors.bind(this);
        this.submitButtonClickHandler = this.submitButtonClickHandler.bind(this);
    }

    async getCompanyInformation() {
        if (!this.state.isAuthenticated || !this.state.billToId) {
            return;
        }

        const getBillToParams: GetBillToApiParameter = {
            billToId: this.state.billToId,
            expand: ["shipTos"],
        };

        const billToModel = await getBillTo(getBillToParams);

        if (phoneRegex.test(billToModel.phone)) {
            await this.setState({ contactNumber: billToModel.phone });
        }

        await this.setState({
            companyName: billToModel.companyName,
            customerNumber: billToModel.customerNumber,
        });
    }

    async resetFormData() {
        await this.setState({
            contactName: this.state.isAuthenticated ? `${this.props.session.firstName} ${this.props.session.lastName}` : "",
            contactEmail: this.state.isAuthenticated ? this.props.session.email : "",
            contactNumber: "",
            companyName: "",
            additionalComments: "",
            files: [],

            contactNameError: "",
            contactEmailError: "",
            contactNumberError: "",
            companyNameError: "",

            inProgress: false,
        });

        await this.getCompanyInformation();
    }

    async openModal() {
        await this.resetFormData();
        this.setState({ modalIsOpen: true });
    }

    async modalCloseHandler() {
        await this.setState({ modalIsOpen: false });
    }

    async checkForErrors() {
        const requiredFieldMessage = translate("This is a required field.");
        const emailFieldMessage = translate("Enter a valid email address.");
        let encounteredError = false;

        if (!emailRegex.test(this.state.contactEmail)) {
            await this.setState({ contactEmailError: emailFieldMessage });
            encounteredError = true;
        }

        if (!this.state.contactName || this.state.contactName === "") {
            await this.setState({ contactNameError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.contactNumber || this.state.contactNumber === "") {
            await this.setState({ contactNumberError: requiredFieldMessage });
            encounteredError = true;
        }

        if (!this.state.companyName || this.state.companyName === "") {
            await this.setState({ companyNameError: requiredFieldMessage });
            encounteredError = true;
        }

        if (this.state.files.length === 0) {
            await this.setState({ filesError: requiredFieldMessage });
            encounteredError = true;
        } else {
            await this.setState({ filesError: "" });
        }

        return encounteredError;
    }

    async submitButtonClickHandler(addToast: Function) {
        const successToast = {
            body: siteMessage("LicenseUploadConfirmationMessage"),
            messageType: "success",
        };

        const dataHasErrors = await this.checkForErrors();

        if (dataHasErrors) {
            return;
        }

        this.setState({ inProgress: true });

        const model: LicenseUploadRequest = {
            contactName: this.state.contactName,
            contactEmail: this.state.contactEmail,
            contactNumber: this.state.contactNumber,
            companyName: this.state.companyName,
            customerNumber: this.state.customerNumber,
            additionalComments: this.state.additionalComments,
            emailTos: this.props.emailRecipients !== undefined ? this.props.emailRecipients.split("//") : [],
            files: this.state.files,
        };

        await sendLicenseUploadRequest(model);
        addToast(successToast);
        this.setState({ modalIsOpen: false, inProgress: false });
    }

    render() {
        return (
            <>
                <Toaster>
                    <ToasterContext.Consumer>
                        {({ addToast }) => (
                            <>
                                <Button
                                    onClick={this.openModal}
                                    variant="tertiary"
                                    data-test-selector="licenseUploadLink"
                                >
                                    {translate("License Upload")}
                                </Button>
                                <Modal
                                    {...this.state.styles.modal}
                                    headline={this.props.title}
                                    isOpen={this.state.modalIsOpen}
                                    handleClose={this.modalCloseHandler}
                                >
                                    <GridContainer {...this.state.styles.container} data-test-selector="licenseUploadForm">
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Typography>{this.props.description}</Typography>
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Contact Name")}
                                                value={this.state.contactName}
                                                onChange={e => this.setState({ contactName: e.target.value, contactNameError: "" })}
                                                error={this.state.contactNameError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Contact Email")}
                                                value={this.state.contactEmail}
                                                onChange={async e => {
                                                    const value = e.target.value;
                                                    if (emailRegex.test(value)) {
                                                        await this.setState({ contactEmail: value, contactEmailError: "" });
                                                    } else {
                                                        await this.setState({ contactEmail: value });
                                                    }
                                                }}
                                                error={this.state.contactEmailError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <PhoneNumberField
                                                label={translate("Contact Number")}
                                                initialValue={this.state.contactNumber}
                                                onChange={this.state.contactNumberOnChange}
                                                update={this.state.contactNumberUpdate}
                                                data-test-selector="pumpRentalContactNumber"
                                                format="000-000-0000"
                                                required={true}
                                                hideRequiredAsterisk={false}
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.twoLinerGridItem}>
                                            <TextField
                                                label={translate("Company Name")}
                                                value={this.state.companyName}
                                                onChange={e => this.setState({ companyName: e.target.value, companyNameError: "" })}
                                                error={this.state.companyNameError}
                                                required
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <TextArea
                                                label={translate("Additional Comments")}
                                                value={this.state.additionalComments}
                                                onChange={e => this.setState({ additionalComments: e.target.value })}
                                            />
                                        </GridItem>
                                        <GridItem {...this.state.styles.uploadGridItem}>
                                            <Typography>{translate("Upload License(s) *")}</Typography>
                                            <input
                                                type="file"
                                                multiple
                                                onChange={e => {
                                                    if (e.target.files) {
                                                        this.setState({
                                                            files: Array.from(e.target.files),
                                                            filesError: "",
                                                        });
                                                    }
                                                }}
                                            />
                                            {this.state.filesError && (
                                                <Typography color="danger">{this.state.filesError}</Typography>
                                            )}
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Typography>{`* ${this.props.requiredFieldsLabel}`}</Typography>
                                        </GridItem>
                                        <GridItem {...this.state.styles.oneLinerGridItem}>
                                            <Button
                                                {...this.state.styles.submitButton}
                                                onClick={() => this.submitButtonClickHandler(addToast)}
                                                disabled={this.state.inProgress}
                                            >
                                                {translate("Submit")}
                                            </Button>
                                        </GridItem>
                                    </GridContainer>
                                </Modal>
                            </>
                        )}
                    </ToasterContext.Consumer>
                </Toaster>
            </>
        );
    }
}

export default connect(mapStateToProps)(LicenseUploadModal);